import { COMFORT, DEFAULTS, RECOMMENDATION } from '@/config/training';
import { getChord } from '@/data/chords';
import type { ChordStats, Recommendation, SessionSummary, TransitionStats } from '@/types/training';
import { clamp } from '@/audio/dsp/stats';
import { formatTransitionKey } from './summary';

export interface RecommendationInput {
  transitions: TransitionStats[];
  chords: ChordStats[];
  sessions: SessionSummary[];
  now: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Deterministic recommendation engine.
 *
 * Every recommendation must be explainable from stored numbers — the
 * `explanationRu` string is built from the same values the rule fired on, so
 * "why am I being told this?" always has a real answer. No model, no API, no
 * randomness.
 *
 * Priority order is fixed: weak transitions, then chord clarity, then rhythm,
 * then spaced review of things that already work.
 */
export function recommend(input: RecommendationInput): Recommendation {
  return (
    weakTransition(input) ??
    lowClarityChord(input) ??
    weakRhythm(input) ??
    spacedReview(input) ??
    firstSession()
  );
}

function firstSession(): Recommendation {
  return {
    mode: 'chordChange',
    chordIds: [...DEFAULTS.starterChords],
    bpm: DEFAULTS.bpm,
    durationMin: DEFAULTS.durationMin,
    reason: 'firstSession',
    explanationRu: 'Два простых аккорда и медленный темп — так проще всего начать.',
  };
}

/**
 * The success rate that decides "weak": the recent window when it holds
 * enough attempts, the lifetime rate otherwise. Ten failures in the first
 * week and ten successes since are a solved transition, not a 50 % one.
 */
export function judgedSuccessRate(stats: TransitionStats): number {
  const recent = stats.recentOutcomes;
  if (recent && recent.length >= RECOMMENDATION.minAttempts) {
    return recent.reduce((sum, outcome) => sum + outcome, 0) / recent.length;
  }
  return stats.successRate;
}

function weakTransition(input: RecommendationInput): Recommendation | null {
  const candidates = input.transitions
    .filter((t) => t.attempts >= RECOMMENDATION.minAttempts)
    .map((t) => ({ stats: t, rate: judgedSuccessRate(t) }))
    .filter(({ rate }) => rate < RECOMMENDATION.weakSuccessRate)
    // Weakest first; ties broken by which was practised longest ago.
    .sort((a, b) => a.rate - b.rate || a.stats.lastPracticedAt - b.stats.lastPracticedAt);

  const worst = candidates[0];
  if (!worst) return null;
  if (!getChord(worst.stats.from) || !getChord(worst.stats.to)) return null;

  return {
    mode: 'chordChange',
    chordIds: [worst.stats.from, worst.stats.to],
    bpm: safeBpm(worst.stats),
    durationMin: RECOMMENDATION.focusDurationMin,
    reason: 'weakTransition',
    transitionKey: worst.stats.key,
    explanationRu: `${formatTransitionKey(worst.stats.key)} получается в ${Math.round(
      worst.rate * 100,
    )} % попыток — это меньше, чем у остальных.`,
  };
}

function lowClarityChord(input: RecommendationInput): Recommendation | null {
  const worst = input.chords
    .filter((c) => c.avgClarity !== null && c.avgClarity < RECOMMENDATION.lowClarity)
    .sort((a, b) => (a.avgClarity ?? 100) - (b.avgClarity ?? 100))[0];
  if (!worst || !getChord(worst.chordId)) return null;

  return {
    mode: 'cleanChord',
    chordIds: [worst.chordId],
    bpm: 0,
    durationMin: RECOMMENDATION.focusDurationMin,
    reason: 'lowClarity',
    explanationRu: `${worst.chordId} звучит на ${Math.round(worst.avgClarity ?? 0)} % чисто — стоит проверить каждую струну.`,
  };
}

function weakRhythm(input: RecommendationInput): Recommendation | null {
  const scored = input.sessions.filter((s) => s.rhythmScore !== null).slice(0, 5);
  if (scored.length === 0) return null;
  const average = scored.reduce((sum, s) => sum + (s.rhythmScore ?? 0), 0) / scored.length;
  if (average >= RECOMMENDATION.weakRhythm) return null;

  return {
    mode: 'rhythm',
    chordIds: [],
    bpm: DEFAULTS.bpm,
    durationMin: RECOMMENDATION.focusDurationMin,
    reason: 'weakRhythm',
    explanationRu: `Ровность за последние тренировки — ${Math.round(average)} %. Немного метронома это поправит.`,
  };
}

function spacedReview(input: RecommendationInput): Recommendation | null {
  const threshold = input.now - RECOMMENDATION.reviewAfterDays * DAY_MS;
  const stale = input.transitions
    .filter((t) => t.attempts >= RECOMMENDATION.minAttempts)
    .filter((t) => t.lastPracticedAt < threshold)
    .sort((a, b) => a.lastPracticedAt - b.lastPracticedAt)[0];
  if (!stale || !getChord(stale.from) || !getChord(stale.to)) return null;

  const days = Math.max(1, Math.round((input.now - stale.lastPracticedAt) / DAY_MS));
  return {
    mode: 'chordChange',
    chordIds: [stale.from, stale.to],
    bpm: safeBpm(stale),
    durationMin: DEFAULTS.durationMin,
    reason: 'spacedReview',
    transitionKey: stale.key,
    explanationRu: `${formatTransitionKey(stale.key)} не повторяли ${days} дн. — самое время освежить.`,
  };
}

/**
 * A tempo the player can actually hold: their comfortable tempo minus a small
 * margin, or the slow default when there is not enough evidence yet.
 */
function safeBpm(stats: TransitionStats): number {
  if (stats.comfortableBpm === null) return DEFAULTS.bpm;
  return clamp(stats.comfortableBpm - RECOMMENDATION.bpmSafetyMargin, COMFORT.bucketSize * 6, 200);
}

/** Reason → i18n key, so the UI can label the recommendation card. */
export function reasonKey(reason: Recommendation['reason']): `reason.${Recommendation['reason']}` {
  return `reason.${reason}`;
}
